import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { 
  HiOutlineX, 
  HiOutlineCheck, 
  HiOutlineExclamation 
} from 'react-icons/hi';
import { updateUserProfile } from '../../store/slices/authSlice';
import { sendProfileUpdateNotification } from '../../emailjs/emailService';
import './ProfileEditForms.scss';

const PersonalInfoForm = ({ isOpen, onClose, userData }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    phone: userData.phone || '',
    email: userData.email || '',
    dob: userData.dob ? new Date(userData.dob.seconds * 1000).toISOString().split('T')[0] : '',
    department: userData.department || '',
    location: userData.location || '',
    languages: userData.languages || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add function to handle modal overlay click
  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Convert date string back to a timestamp for Firebase
      const updatedData = {
        ...formData,
        // Only include fields that have actually changed
        ...Object.keys(formData).reduce((acc, key) => {
          if (formData[key] !== userData[key]) {
            if (key === 'dob' && formData[key]) {
              // Convert date string to a timestamp-like object for Firebase
              const dobDate = new Date(formData[key]);
              acc[key] = { seconds: Math.floor(dobDate.getTime() / 1000), nanoseconds: 0 };
            } else {
              acc[key] = formData[key];
            }
          }
          return acc;
        }, {})
      };

      // Only dispatch if there are changes
      if (Object.keys(updatedData).length > 0) {
        await dispatch(updateUserProfile({
          email: userData.email,
          profileData: updatedData
        })).unwrap();
        
        // Send profile update notification email
        try {
          await sendProfileUpdateNotification(
            userData,
            'personal_info',
            updatedData
          );
          console.log('Profile update notification sent successfully');
        } catch (emailError) {
          console.error('Failed to send profile update notification:', emailError);
          // Don't fail the whole operation if email fails
        }
        
        setSuccess('Personal information updated successfully!');
        // Close the modal after a short delay
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setSuccess('No changes detected');
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update personal information. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="profile-edit-modal">
      <div className="modal-overlay" onClick={handleOverlayClick}></div>
      <div className="modal-container">
        <div className="modal-header">
          <h2>Edit Personal Information</h2>
          <button className="close-button" onClick={onClose}>
            <HiOutlineX />
          </button>
        </div>

        {error && (
          <div className="alert error">
            <HiOutlineExclamation />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="alert success">
            <HiOutlineCheck />
            <span>{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="edit-form">
          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter your phone number"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              readOnly
            />
            <small className="field-note">Email cannot be changed</small>
          </div>

          <div className="form-group">
            <label htmlFor="dob">Date of Birth</label>
            <input
              type="date"
              id="dob"
              name="dob"
              value={formData.dob}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="department">Department</label>
            <input
              type="text"
              id="department"
              name="department"
              value={formData.department}
              onChange={handleChange}
              placeholder="Enter your department"
            />
          </div>

          <div className="form-group">
            <label htmlFor="location">Location</label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Enter your location"
            />
          </div>

          <div className="form-group">
            <label htmlFor="languages">Languages</label>
            <input
              type="text"
              id="languages"
              name="languages"
              value={formData.languages}
              onChange={handleChange}
              placeholder="Enter languages (comma separated)"
            />
            <small className="field-note">Example: English, Hindi, Spanish</small>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-button" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="save-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonalInfoForm; 